import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { BaseAutomationDiscoveryDialog } from '../BaseAutomationDiscoveryDialog.tsx';
import { EventSourceName } from '../utils/analytics.ts';

export const i18n = defineMessages({
	productRequirementsModalHeader: {
		id: 'automation-discovery.rovo.product-requirements.modal.header',
		defaultMessage: '{publishedPageTitle} was successfully published in the {spaceName} space',
		description:
			'This text is displayed as a title in a modal that says their page was published in the specified space',
	},
	productRequirementsCta: {
		id: 'automation-discovery.rovo.product-requirements.modal.cta',
		defaultMessage: 'Automate your product requirements work?',
		description:
			'This text is displayed in a section to ask the user if they want to automatically create a product requirements automation rule',
	},
	productKeywordForModalTrigger: {
		id: 'automation-discovery.rovo.product-requirements.product.trigger.keyword',
		defaultMessage: 'product',
		description: 'Keyword in page title that triggers automation discovery dialog',
	},
	requirementKeywordForModalTrigger: {
		id: 'automation-discovery.rovo.product-requirements.requirement.trigger.keyword',
		defaultMessage: 'requirement',
		description: 'Keyword in page title that triggers automation discovery dialog',
	},
});

type Intl = {
	formatMessage: (messageDescriptor: { id: string; defaultMessage: string }) => string;
};

export const isEligibleForProductRequirementsDiscovery = (
	pageTitle: string,
	intl: Intl,
): boolean => {
	return (
		pageTitle.toLowerCase().includes(intl.formatMessage(i18n.productKeywordForModalTrigger)) &&
		pageTitle.toLowerCase().includes(intl.formatMessage(i18n.requirementKeywordForModalTrigger))
	);
};

type ProductRequirementsDialogProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	publishedPageTitle: string;
	spaceName: string;
	spaceKey?: string;
};

const ProductRequirementsDialogComponent = ({
	onClose,
	publishedPageTitle,
	spaceName,
	spaceKey,
}: ProductRequirementsDialogProps) => {
	return (
		<BaseAutomationDiscoveryDialog
			eventSourceName={EventSourceName.PRODUCT_REQUIREMENTS}
			templateName="addPRDImprovementCommentAfterPagePublished"
			discoveryExperience="automationDiscoveryProductRequirements"
			onClose={onClose}
			cta={<FormattedMessage {...i18n.productRequirementsCta} />}
			header={
				<FormattedMessage
					{...i18n.productRequirementsModalHeader}
					values={{ publishedPageTitle, spaceName }}
				/>
			}
			localStorageKey={keys.AUTOMATION_DISCOVER_PRODUCT_REQUIREMENTS}
			templateUrl={`/wiki/spaces/${spaceKey}/settings/automation#/rule/template?templateId=confluence_rovo_template_1`}
			spaceKey={spaceKey}
		/>
	);
};

export const ProductRequirementsDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(ProductRequirementsDialogComponent);
